import React, {ReactElement, useState, useEffect, useRef, useMemo} from 'react';
import Head from 'next/head';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import axios from 'axios';

import MainLayout from 'components/layout/mainLayout';
import PageWrapper from 'components/pageWrapper';
import ContentWrapper from 'components/contentWrapper';
import ContentArea from 'components/contentArea';
import Button from 'components/button';
import BlockGoogleMap from 'components/blocks/blockGoogleMap';
import Checkbox from 'components/forms/checkbox';

import config from 'settings/config';
import theme, {bp, color} from 'settings/theme';

import seo from 'utils/seo';

import getTemplateData from 'utils/getTemplateData';
import storyblokService from 'services/storyblokService.js';

import replaceNewLinesWithBr from 'utils/replaceNewLinesWithBr'


type Props = {
  statusCode: number,
  error?: any,
  masterTemplateData: any,
  pageData: any
};


type StyledProps = {
  isSubmitting: boolean
};


// Page
const Page: NextPageExtended<Props> = (props) => {

  const [show, setShow] = useState<string>('form');
  const [marketingConsent, setMarketingConsent] = useState(false);

  const { register, handleSubmit, watch, formState: { errors }, formState: { isSubmitted, isSubmitting, submitCount } } = useForm();

  // Get content from supplied StoryBlok story
  const content = useMemo(() => {
    return props?.pageData?.content;
  }, [props.pageData]);

  const seoContent = useMemo(() => {
    return props.pageData?.content?.seo;
  }, [props.pageData]);
  

  // Page Load
  useEffect(() => {
    storyblokService.initEditor(null);
  }, []);


  // Form Submitted
  const onSubmit = async (data) => {

    data.marketingConsent = marketingConsent;

    const contactResponse = await axios.post(`${config.apiUrl}/contact`, data, {});

    if(contactResponse.status < 300){
      setShow('thanks');
    }
    else{
      setShow('error');
    }

  }


  // Checkbox Changed
  const handleCheckboxChange = (e:React.ChangeEvent<HTMLInputElement>) => {
    setMarketingConsent(!marketingConsent);
  }

  
  return (

    <>

      <Head>
        <title>{seo.createTitle(seoContent?.title)}</title>
        <meta name="description" content={seoContent?.description} />
      </Head>

      <StyledPage isSubmitting={isSubmitted} className="contactPage">

        <PageWrapper>

          {/* Top Section */}
          <div className="contactPage-detailsSection paddingTop paddingBottom">
            <ContentWrapper>
                
              <h1 className="spacingBottomHalf">{content.title}</h1>
              <div className="spacingBottomHalf" dangerouslySetInnerHTML={{ __html: replaceNewLinesWithBr(content.intro) }} />

              <div className="contactPage-columns">
                <div className="contactPage-details">

                  <div className="contactPage-details-phone">

                    <div className="contactPage-details-phone-text">
                      <h3>Phone</h3>
                      <div>
                        <a href={`tel:${content.phone_number}`}>{content.phone_number}</a>
                      </div>
                    </div>

                    <div className="contactPage-details-phone-button">
                      <Button
                          as="a"
                          href={`tel:${content.call_button_phone_number.replace(/\s/g, '')}`}
                          height="30px"
                          corners="4px"
                          color="green"
                          horizontalPadding="8px"
                          className="contactPage-details-phone-button-button"
                      >
                        CALL NOW
                      </Button>
                    </div>

                  </div>

                  <div className="contactPage-details-email">
                    <h3>Email Address</h3>
                    <div>
                      <a href={`mailto:${content.email}`}>{content.email}</a>
                    </div>
                  </div>

                  <div className="contactPage-details-address">
                    <h3>Address</h3>
                    <div>{replaceNewLinesWithBr(content.address)}</div>
                    <div className="contactPage-details-address-note" dangerouslySetInnerHTML={{ __html: replaceNewLinesWithBr(content.address_note) }} />
                  </div>

                </div>

                <div className="contactPage-opening">
                  <h3>Opening Times</h3>
                  <div dangerouslySetInnerHTML={{ __html: replaceNewLinesWithBr(content.opening_times) }} />
                  <div className="contactPage-opening-button">
                      <Button
                          as="a"
                          href="https://calendly.com/wheelfreedom-showroom?utm_source=contactuspage"
                          height="40px"
                          corners="4px"
                          color="orange"
                          horizontalPadding="16px"
                      >
                        BOOK AN APPOINTMENT
                      </Button>
                    </div>
                </div>

              </div>

            </ContentWrapper>
          </div>


          {/* Form Section */}
          <div className="contactPage-formSection paddingTop">
            <ContentWrapper>

              <h2 className="spacingBottomHalf">{content.form_title}</h2>
              <div className="spacingBottom">
                <div dangerouslySetInnerHTML={{ __html: replaceNewLinesWithBr(content.form_intro) }} />
              </div>

              {/* Form */}
              {(show === 'form' || show === 'error') &&
                <form onSubmit={handleSubmit(onSubmit)}>
                    
                  <div className="formRow">
                    <label>
                        <div className="formlabel">Your Name *</div>
                        <input
                          {...register("name", { required: 'This is required' })}
                          className="textInput"
                        />
                    </label>
                    {errors.name && 
                      <div className="formError">Please enter your name</div>
                    }
                  </div>

                  <div className="formRow">
                    <label>
                      <div className="formlabel">Your Email Address *</div>
                      <input
                        {...register("emailAddress", { required: 'This is required' })}
                        className="textInput"
                      />
                    </label>
                    {errors.emailAddress && 
                      <div className="formError">Please enter your email address</div>
                    }
                  </div>

                  <div className="formRow">
                    <label>
                      <div className="formlabel">Your Phone Number *</div>
                      <input
                        {...register("phoneNumber", { required: 'This is required' })}
                        className="textInput"
                      />
                    </label>
                    {errors.phoneNumber && 
                      <div className="formError">Please enter your phone number</div>
                    }
                  </div>

                  <div className="formRow">
                    <label>
                      <div className="formlabel">Your Message*</div>
                      <textarea
                        {...register("message", { required: 'This is required' })}
                      />
                    </label>
                    {errors.message && 
                      <div className="formError">Please enter a message before continuing</div>
                    }
                  </div>

                  <div className="formRow">
                    <Checkbox checked={marketingConsent} onChange={handleCheckboxChange}>
                      Please send me interesting news and offers
                    </Checkbox>
                  </div>


              
                  
                  <div className="contactPage-form-submit">

                    <Button
                      type="submit"
                      corners="4px"
                      color="blue"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? `sending...` : `SEND MESSAGE`}
                    </Button>

                    {show === 'error' &&
                      <div className="contactPage-form-postError">Sorry, there was an error sending your message, please try again later or call us on the number above</div>
                    }

                  </div>

                </form>
              }

              {/* Thank You  */}
              {show === 'thanks' &&
                <div className="contactPage-form-thanks" dangerouslySetInnerHTML={{ __html: replaceNewLinesWithBr(content.form_thank_you_message) }} />
              }

            </ContentWrapper>
          </div>

          {/* Map Section */}
          <div>
            <BlockGoogleMap block={{zoomLevel: 10, query: content.map_search_term}} />
          </div>

        </PageWrapper>

      </StyledPage>
      
      {storyblokService.bridge()}

    </>
  
  );

}


// Server Side Props
export async function getServerSideProps({ params, preview = false, previewData = {} }) {

  let statusCode = 200;

  // Get Data
  let results;

  try {
    results = await Promise.all([
      getTemplateData(),
      storyblokService.get(`cdn/stories/contact`, {})
    ]);
  } catch (error) {
    return {
      // revalidate: 1, 
      props: { statusCode: error.response ? error.response.status : 500}
    };
  }

  const masterTemplateData = results[0] ?? null;
  const pageData = results[1]?.data?.story ?? null;

  // Hack because last items keeps going missing
  masterTemplateData.content.top_menu_categories.push({});

  // Return props
  return {
    // unstable_revalidate: 1,
    props: {
        statusCode,
        masterTemplateData,
        pageData
    }
  };

}

Page.getLayout = (page: ReactElement) => {
  return (
    <MainLayout masterTemplateData={page.props.masterTemplateData}>
      {page}
    </MainLayout>
  );
}

export default Page;




// Styles
const StyledPage = styled.div<StyledProps>`

  .contactPage-detailsSection{
    border-bottom: solid 1px ${color.lines};
    background-color: white;
  }

  .contactPage-columns{

    display: flex;
    flex-direction: column;
    gap: 2rem;

  }

  .contactPage-details{

    display: flex;
    flex-direction: column;
    gap: 1rem;

    h3{
      margin-bottom: 0.5rem;
      font-size: 16px;
    }

    .contactPage-details-phone{

      display: flex;
      width: 100%;

      .contactPage-details-phone-text{
        flex: 1 1 auto;
      }

      .contactPage-details-phone-button{
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-left: 5px;
      }

      .contactPage-details-phone-button-button{
        font-family: ${theme.typography.fontFamilySecondary};
      }

    }

    .contactPage-details-address-note{
      margin-top: 0.5rem;
      font-size: 0.8rem;
    }

  }

  .contactPage-opening{
    .contactPage-opening-button{
      margin-top: 1rem;
    }
  }


  form{

      .formRow{
          margin-bottom: 1.5rem;
      }

      .formlabel{
          margin-bottom: 0.2rem;
          font-family: ${theme.typography.fontFamilySecondary};
          font-weight: bold;
      }

      .formError{
          margin-top: 0.2rem;
          color: red;
      }

      .textInput{

          width: 100%;
          height: 40px;
          padding: 0 8px;
          border-radius: 0;
          border: solid 1px ${color.grey(80)};

          &:focus{
              outline: none;
              border: solid 1px ${color.grey(50)};
          }

      }

      textarea{

          width: 100%;
          height: 150px;
          padding: 8px 8px;
          border-radius: 0;
          border: solid 1px ${color.grey(80)};

          &:focus{
              outline: none;
              border: solid 1px ${color.grey(50)};
          }

      }

  }

  .contactPage-formSection{
      padding-bottom: 4rem;
  }

  .contactPage-form-submit{

      .button{
          width: 100%;
          margin-top: 1.5rem;
          font-family: ${theme.typography.fontFamilySecondary};
      }
      
  }

  .contactPage-form-postError{
      margin-top: 0.5rem;
      color: red;
  }

  .contactPage-form-thanks{
      font-weight: bold;
  }



  @media (min-width: ${bp(3)}) {

    .contactPage-columns{
      flex-direction: row;
      gap: 6rem;
      margin-top: 4rem;

      > *{
        flex: 1;
      }

    }

    .contactPage-details{
      gap: 1.5rem;
    }

    form{

      .textInput{
        max-width: 600px;
      }

      textarea{
        max-width: 600px;
      }
        
    }

    .contactPage-form-submit{

      .button{
        width: auto;
        min-width: 200px;
      }

    }

  }

  @media (min-width: 500px) {

      .contactPage-details{

          .contactPage-details-phone{

              .contactPage-details-phone-button{
                  display: none;
              }

          }

      }
      

  }

`